import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Button, CircularProgress, useSnackbar } from '@packages/shared';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Wishlist32 } from '@packages/themes/icons';
import { defineMessages, useIntl } from 'react-intl';
import { bridge } from '@empiriecom/mybuy-session/Bridge';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import type { GTMEventGlycerinRemoveFromCart } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { RemoveFromBasketDocument } from '@packages/gql/generated/shopping/RemoveFromBasketDocument';
import { AddToWishlistDocument } from '@packages/gql/generated/shopping/AddToWishlistDocument';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { buttonStyling, iconStyling } from './Actions.styles';
import { useEventTracking } from '../Tracking/useEventTracking';

export interface AddToWishlistActionProps {
  lineItemId: LineItemFragmentFragment['id'];
  sku: LineItemFragmentFragment['product']['sku'];
  glycerinProduct: GlycerinCartProduct;
  bridge?: any; // TODO replace this with stronger type inference
}
const messages = defineMessages({
  buttonText: {
    id: 'AddToWishlistAction.ButtonText',
    defaultMessage: 'Merken',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'AddToWishlistAction.SuccessMessage',
    defaultMessage: 'Dein Artikel wurde erfolgreich auf den Merkzettel verschoben.',
    description: 'this will be displayed on notification provider',
  },
});

export const AddToWishlistAction: React.FC<AddToWishlistActionProps> = ({
  lineItemId,
  sku,
  glycerinProduct,
}) => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useEventTracking();
  const dispatchGlycerin = useTracking();
  const [basketState, executeBasketMutation] = useAuthMutation(RemoveFromBasketDocument);
  const [wishlistState, executeWishlistMutation] = useAuthMutation(AddToWishlistDocument);
  const { addToast } = useSnackbar();

  const onAddWishlistHandler = () => {
    if (!basketState.fetching && lineItemId && !wishlistState.fetching && sku) {
      // we still need implementation for bridge
      // istanbul ignore next
      if (bridge?.updateWishlist) bridge.updateWishlist();
      // istanbul ignore next
      if (bridge?.updateBasket) bridge.updateBasket();
      // We need to wait for both mutations to finish before we can decide if it was successful or not
      Promise.allSettled([
        executeWishlistMutation({ locale, sku, unspecified: false }),
        executeBasketMutation({ lineItemId, locale }),
      ]).then(([wishlistResult, basketResult]) => {
        if (wishlistResult.status === 'fulfilled' && basketResult.status === 'fulfilled') {
          const basket = getFragmentData(
            BasketFragmentFragmentDoc,
            basketResult.value.data?.removeFromBasket,
          );

          if (basket && basket.success) {
            addToast({
              severity: 'success',
              message: formatMessage(messages.successMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: {
                removedProducts: [glycerinProduct],
              },
            });
            // display cart is fired from basket component due to its rerendering
            dispatch('basket_article', 'addToWishlist');
          }
        }
      });
    }
  };
  return (
    <Button
      sx={buttonStyling}
      onClick={() => onAddWishlistHandler()}
      data-testid="wishlist-action"
      variant="text"
      startIcon={
        basketState.fetching && wishlistState.fetching ? (
          <CircularProgress />
        ) : (
          <Wishlist32 sx={iconStyling} />
        )
      }
    >
      {formatMessage(messages.buttonText)}
    </Button>
  );
};
